// Role of this file:
// Listen for an action
// produce a side effect which is communicate with the server via http,
// bring data back and dispatch a new action (success or fail)

import { Injectable } from "@angular/core";

// Actions is an observable that listens to the types of actions
// being dispatched and we can respond accordingly
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { from, Observable, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, take } from "rxjs/operators";

import { WarningDialogComponent } from "#core/components/content-library/assign-cart/components/warning-dialog/warning-dialog.component";
import { SnackBarService } from "#services/snackbar.service";
import { LtiReceiverService } from "#services/lti-receiver.service";
import { MatDialog } from "@angular/material/dialog";
import { AssignmentsService } from "@carnegie-learning/data-access-angular-passport";
import { Asset } from "@carnegie-learning/cure-types";
import * as LMSIStore from "../../../../lmsi/store";
import * as assignActions from "../actions/selected-students.action";

@Injectable()
export class SelectedAssignablesEffects {
    working = false;

    constructor(
        private actions$: Actions,
        private snackBarService: SnackBarService,
        private assignmentsService: AssignmentsService,
        private ltiReceiverService: LtiReceiverService,
        private lmsiStore: Store<LMSIStore.LaunchState>,
        public dialog: MatDialog
    ) {}

    assign$ = createEffect(() =>
        this.actions$.pipe(ofType(assignActions.assign)).pipe(
            map((action) => action.payload),
            mergeMap((assignment) => {
                this.working = true;
                if (this.working) {
                    this.snackBarService.working("Working...");
                }
                return this.assignmentsService
                    .createAssignments(assignment.assets, assignment.students)
                    .pipe(
                        // Surface success UI before the DL handoff, since the
                        // DL replay navigates the browser away and tears down Angular.
                        map((response) => {
                            this.working = false;
                            this.snackBarService.success(
                                "Successfully Assigned!"
                            );
                            return response;
                        }),
                        switchMap((response) =>
                            this.maybeSubmitDeepLinkResponse(assignment.assets).pipe(
                                map(() => response)
                            )
                        ),
                        map((response) =>
                            assignActions.assignSuccess({ payload: response })
                        ),
                        catchError((error) => {
                            this.working = false;
                            if (error.error.hasOwnProperty("case")) {
                                this.snackBarService.success(
                                    "Successfully Assigned!"
                                );
                                const dialogRef = this.dialog.open(
                                    WarningDialogComponent,
                                    {
                                        width: "40vw",
                                        maxWidth: "700px",
                                        minWidth: "300px",
                                        data: {
                                            msg: error.error.message,
                                            case: error.error.case,
                                        },
                                    }
                                );
                            } else {
                                this.snackBarService.success(
                                    "Failed To Assign!"
                                );
                            }

                            return of(
                                assignActions.assignFail({ payload: error })
                            );
                        })
                    );
            })
        )
    );

    /**
     * XOP-662: when the teacher's session was launched from an LMS via LTI 1.3
     * Deep Linking, the launchContext (set in lmsi store from the rex launch
     * URL) carries a `launchId`. After Passport has internally created the
     * assignment, ask lti-receiver to mint a signed DL response JWT and replay
     * it through the teacher's browser to the LMS's deep_link_return_url,
     * which creates the corresponding LMS-side assignment.
     *
     * No-op when launchId is absent — the existing non-LTI flow runs as before.
     */
    private maybeSubmitDeepLinkResponse(assets: Asset[]): Observable<void> {
        return this.lmsiStore.pipe(
            select(LMSIStore.getLaunchContext),
            take(1),
            switchMap((launchContext) => {
                const launchId =
                    (launchContext?.applicationContext as any)?.launchId;
                if (!launchId) { return of(undefined as void); }
                return from(
                    this.ltiReceiverService.submitDeepLinkResponse(launchId, assets)
                ).pipe(
                    map((result) => {
                        if (!result.success) {
                            console.error(
                                "LTI Deep Linking response failed",
                                result.error
                            );
                        }
                        return undefined as void;
                    })
                );
            })
        );
    }
}
